import type { AccountRemovalBlockers } from 'components/account/AccountRemovalPrevented';
import AuthManager from 'components/auth/AuthManager';
import { CountryCode, Lang } from 'types/generatedSchemaTypes';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { zustandMMKVStorage } from './utils/commonStoreUtils';

export type AuthConfig = 'AuthConfigSuSi' | 'AuthConfigSuSiTest';

type FcmRegistration = {
  token: string | null;
  lang: Lang;
};

const emptyAccountRemovalBlockers: AccountRemovalBlockers = {
  ongoingParking: undefined,
  ongoingCharging: undefined,
  failedPayments: undefined,
  prepaidTickets: undefined,
  permits: undefined,
};

type AuthTokens = {
  accessToken: string | null;
  refreshToken: string | null;
  expireTime: string | null;
  loginLoading: boolean;
  deviceToken?: string | null;
  fcmRegistration?: FcmRegistration | null;
};

type Auth = AuthTokens & {
  // Account domain (migrated from Apollo reactive vars in states/common).
  // In-memory only (not persisted) to preserve the reactive-var behavior of
  // resetting on app restart.
  currentCustomerCountry: CountryCode | null;
  acceptedTermsAfterFeatureCountryChange: boolean;
  accountRemovalBlockers: AccountRemovalBlockers;
  accountRemoveWithWarnings: boolean;
  accountRestricted: boolean;
};

type AuthActions = {
  setTokens: (values: AuthTokens) => void;
  setDeviceToken: (deviceToken: string | null) => void;
  setLoginLoading: (loading: boolean) => void;
  setFcmRegistration: (fcmRegistration: FcmRegistration) => void;
  fetchDeviceToken: () => Promise<string | null>;
  logout: () => void;
  setCurrentCustomerCountry: (country: CountryCode | null) => void;
  setAcceptedTermsAfterFeatureCountryChange: (accepted: boolean) => void;
  setAccountRemovalBlockers: (blockers: AccountRemovalBlockers) => void;
  setAccountRemoveWithWarnings: (canContinue: boolean) => void;
  setAccountRestricted: (restricted: boolean) => void;
};

export const useAuthStore = create<Auth & AuthActions>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      expireTime: null,
      deviceToken: null,
      hasMigratedFromLegacyState: false,
      loginLoading: false,
      fcmRegistration: null,
      currentCustomerCountry: null,
      acceptedTermsAfterFeatureCountryChange: false,
      accountRemovalBlockers: emptyAccountRemovalBlockers,
      accountRemoveWithWarnings: false,
      accountRestricted: false,
      setTokens: (values) => {
        if (values.refreshToken) {
          set({
            refreshToken: values.refreshToken,
          });
        }
        set({
          expireTime: values.expireTime,
          accessToken: values.accessToken,
        });
      },
      setDeviceToken: (newToken: string | null) => {
        set({ deviceToken: newToken });
      },
      setFcmRegistration: (fcmRegistration: FcmRegistration) => {
        set({ fcmRegistration });
      },
      setLoginLoading: (loading: boolean) => {
        set({ loginLoading: loading });
      },
      fetchDeviceToken: async () => {
        const fetchedToken = await AuthManager.fetchDeviceToken();
        set({ deviceToken: fetchedToken });
        return fetchedToken;
      },
      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          expireTime: null,
          deviceToken: null,
          fcmRegistration: null,
        });
        useAuthStore.persist.clearStorage();
      },
      setCurrentCustomerCountry: (country) => {
        set({ currentCustomerCountry: country });
      },
      setAcceptedTermsAfterFeatureCountryChange: (accepted) => {
        set({ acceptedTermsAfterFeatureCountryChange: accepted });
      },
      setAccountRemovalBlockers: (blockers) => {
        set({ accountRemovalBlockers: blockers });
      },
      setAccountRemoveWithWarnings: (canContinue) => {
        set({ accountRemoveWithWarnings: canContinue });
      },
      setAccountRestricted: (restricted) => {
        set({ accountRestricted: restricted });
      },
    }),
    {
      name: 'auth-storage', // unique name for localStorage key
      storage: createJSONStorage(() => zustandMMKVStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expireTime: state.expireTime,
        deviceToken: state.deviceToken,
        fcmRegistration: state.fcmRegistration,
      }),
    }
  )
);
